import { css } from "@emotion/css";
import { ArrowRightIcon } from "@octopusdeploy/design-system-icons";
import { borderRadius, borderWidth, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { TimelineItem } from "@octopusdeploy/octopus-server-client";
import React, { useEffect, useMemo, useState } from "react";
import TaskStatusIcon from "~/areas/projects/components/TaskStatusIcon/index";
import { DeploymentsDropDownMenu } from "~/areas/tasks/components/Task/Kubernetes/components/DeploymentsDropDownMenu";
import type { Item } from "~/primitiveComponents/form/Select/Select";
import DateFormatter from "~/utils/DateFormatter/index";
type DiffDeploymentSelectorProps = {
    currentDeployment: TimelineItem;
    comparableDeployments: TimelineItem[];
    onSelectionChanged: (fromDeploymentId: string) => void;
};
const styles = {
    container: css({
        display: "flex",
        flexDirection: "row",
        gap: space[24],
        padding: space[8],
        borderRadius: borderRadius["medium"],
        border: `${borderWidth[1]} solid ${themeTokens.color.border.primary}`,
        backgroundColor: themeTokens.color.background.secondary.default,
    }),
    label: css({
        font: text.body.regular.small,
        color: themeTokens.color.text.primary,
        paddingBottom: space[4],
    }),
    itemContainer: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        gap: space[8],
    }),
    itemTextContainer: css({
        display: "flex",
        flexDirection: "column",
    }),
    itemTitle: css({
        font: text.body.bold.large,
        color: themeTokens.color.text.primary,
    }),
    itemSubtitle: css({
        font: text.body.regular.xSmall,
        lineHeight: "1rem", //NOTE: this is not available in the theme-tokens (xs=1.25).
        color: themeTokens.color.text.secondary,
    }),
    gridContainer: css({
        display: "grid",
        gridTemplateColumns: ["1fr min-content 1fr"],
        gridTemplateRows: ["min-content min-content"],
    }),
    alignArrowCentrally: css({
        display: "flex",
        alignItems: "center",
        paddingLeft: space[24],
        paddingRight: space[24],
    }),
};
type DeploymentItem = Item & {
    timelineItem: TimelineItem;
};
function isDeploymentItem(item: Item): item is DeploymentItem {
    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return (item as DeploymentItem).timelineItem !== undefined;
}
export const DiffDeploymentSelector = (props: DiffDeploymentSelectorProps) => {
    const [fromExecutionId, setFromExecutionId] = useState<string>(props.comparableDeployments[0].Id); //TODO(tmm): this could fail if comparableDeployments is empty
    // This is used to propagate a "calculate diffs" to the parent (but only on first render)
    useEffect(() => {
        props.onSelectionChanged(fromExecutionId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const timelineMenuItems = useMemo(() => props.comparableDeployments.map((item) => ({
        value: item.Id,
        text: `${item.Version} ${item.Iteration}`,
        icon: <TaskStatusIcon iconOnly size={28} item={{ HasPendingInterruptions: item.HasPendingInterruptions, HasWarningsOrErrors: item.HasWarningsOrErrors, IsCompleted: item.IsCompleted, State: item.TaskState }}/>,
        timelineItem: item,
    })), [props.comparableDeployments]);
    const onFromExecutionIdChanged = (value: string) => {
        if (value !== fromExecutionId) {
            setFromExecutionId(value);
            props.onSelectionChanged(value);
        }
    };
    const renderDeployment = (menuItem: Item) => {
        if (!isDeploymentItem(menuItem)) {
            throw new Error("Menu item does not have a timeline item associated with it");
        }
        const timelineItem = menuItem.timelineItem;
        return <TimeLineItemCard timelineItem={timelineItem}/>;
    };
    return (<div className={styles.gridContainer}>
            <span className={styles.label}>Select the manifests you want to compare:</span>
            <div />
            <span className={styles.label}>with the current deployment</span>
            <div className={styles.container}>
                <DeploymentsDropDownMenu value={fromExecutionId} onChange={onFromExecutionIdChanged} items={timelineMenuItems} selectionRenderer={(_, menuItem) => renderDeployment(menuItem)} menuItemRenderer={renderDeployment}/>
            </div>
            <div className={styles.alignArrowCentrally}>
                <ArrowRightIcon size={24}/>
            </div>
            <div className={styles.container}>
                <TimeLineItemCard timelineItem={props.currentDeployment}/>
            </div>
        </div>);
};
export const TimeLineItemCard = (props: {
    timelineItem: TimelineItem;
}) => (<div className={styles.itemContainer}>
        <TaskStatusIcon key={props.timelineItem.Id} iconOnly size={28} item={{ ...props.timelineItem, State: props.timelineItem.TaskState }}/>
        <div className={styles.itemTextContainer}>
            <div className={styles.itemTitle}>
                {props.timelineItem.Version} {props.timelineItem.Iteration}
            </div>
            <div className={styles.itemSubtitle}>{DateFormatter.dateToShortFormat(props.timelineItem.Completed || props.timelineItem.Created)}</div>
        </div>
    </div>);
